<template>
  <div class="attribute-filter" :class="{ 'has-error': error, compact }">
    <select class="filter-control operator-control" :value="condition.operator" @change="changeOperator($event.target.value)">
      <option v-for="option in operators" :key="option.value" :value="option.value">{{ option.label }}</option>
    </select>

    <template v-if="condition.operator === 'in'">
      <!-- Multiselect buscable: solo cuando el caller pasó un fetcher de
           valores distintos Y ese proveedor los soporta (ver
           services/layers/distinctValues.js). Si no, cae a la caja de texto
           libre de siempre — la feature nunca rompe el filtro por un
           servicio caído o una capa sin soporte. @search solo se conecta
           cuando la lista ya vino truncada (distinctExceeded): con la lista
           completa, el filtro client-side de TagMultiSelect alcanza. -->
      <TagMultiSelect
        v-if="useMultiSelect"
        class="in-multiselect"
        :value="values"
        :options="selectOptions"
        :loading="distinctLoading"
        :show-count="false"
        :max-chips="3"
        placeholder="Buscar valores…"
        :empty-message="distinctExceeded ? 'Mostrando los primeros valores' : 'Sin valores'"
        @input="onMultiSelectInput"
        @search="distinctExceeded ? loadDistinctValues($event) : undefined"
      />
      <div v-else class="multi-value-control">
        <div v-if="values.length" class="chips">
          <span v-for="(value, index) in values" :key="`${value}-${index}`" class="chip">
            {{ displayValue(value) }}
            <button type="button" title="Quitar valor" aria-label="Quitar valor" @click="removeValue(index)">
              <font-awesome-icon icon="trash-alt" />
            </button>
          </span>
        </div>
        <input class="filter-control" v-model="multiInput" :disabled="values.length >= 50"
          placeholder="Valor + Enter" @keydown.enter.prevent="commitValuesAndApply" @keydown.,.prevent="commitValues"
          @paste="onPaste" @blur="commitValues" />
      </div>
    </template>

    <select v-else-if="fieldType === 'boolean' && requiresValue" class="filter-control"
      :value="condition.value" @change="changeValue($event.target.value)">
      <option value="">Seleccione</option>
      <option value="true">Sí</option>
      <option value="false">No</option>
    </select>

    <input v-else-if="requiresValue" class="filter-control" :type="inputType" :value="condition.value"
      @input="changeValue($event.target.value)" @keydown.enter.prevent="$emit('apply')" />

    <span v-if="error" class="filter-error">{{ error }}</span>
  </div>
</template>

<script>
import TagMultiSelect from "@/components/common/pickers/TagMultiSelect.vue";

export default {
  name: "AttributeFilter",
  components: { TagMultiSelect },
  props: {
    fieldType: { type: String, default: "text" },
    filter: { type: Object, default: () => ({ operator: "in", value: [] }) },
    error: { type: String, default: "" },
    compact: { type: Boolean, default: false },
    // field/distinctValuesFetcher habilitan el multiselect buscable del
    // operador "in" - ver services/layers/distinctValues.js. Se pasa una
    // función ya atada a una capa concreta (no la capa/metadata cruda) para
    // que AdvancedFilterPanel.vue pueda atar cada condición del filtro
    // cruzado a SU capa (fuente vs. objetivo) sin que este componente sepa
    // nada de capas.
    field: { type: String, default: "" },
    distinctValuesFetcher: { type: Function, default: null },
    // autoApply: la fila de filtro rápido de AttributesTable.vue quiere que
    // elegir un valor del multiselect "En uno de" aplique de inmediato (sin
    // depender de la lupa) — a diferencia del panel avanzado
    // (AdvancedAttributeGroup.vue), donde se arman varias condiciones antes
    // de aplicar y un apply prematuro por cada tag sería molesto.
    autoApply: { type: Boolean, default: false },
  },
  data() {
    return {
      multiInput: "",
      distinctOptions: [],
      distinctLoading: false,
      // null = todavía no se probó; true/false = resultado del último fetch.
      distinctSupported: null,
      distinctExceeded: false,
      distinctRequest: null,
    };
  },
  computed: {
    useMultiSelect() {
      return !!this.distinctValuesFetcher && this.distinctSupported !== false;
    },
    // Unión de las opciones recién buscadas MÁS los valores ya seleccionados.
    // TagMultiSelect resuelve los chips buscando cada id dentro de `options`
    // y descarta silenciosamente los que no encuentra (selectedOptions,
    // TagMultiSelect.vue) - sin esta unión, un valor ya elegido que no venga
    // en la búsqueda actual (filtro restaurado, o el fetch todavía no
    // resolvió) desaparece del chip aunque siga en el filtro real.
    selectOptions() {
      const known = new Set(this.distinctOptions);
      const missing = this.values.filter((value) => !known.has(value));
      return [...this.distinctOptions, ...missing].map((value) => ({ id: value, label: this.displayValue(value) }));
    },
    condition() {
      return this.filter && this.filter.operator ? this.filter : this.defaultCondition();
    },
    operators() {
      const common = [
        { value: "eq", label: "=" },
        { value: "neq", label: "<>" }
      ];
      if (this.fieldType === "text" || this.fieldType === "unknown") {
        common.unshift(
          { value: "contains", label: "Contiene" },
          { value: "like", label: "Coincide con" }
        );
      }
      if (this.fieldType === "number" || this.fieldType === "date") {
        common.push(
          { value: "gt", label: ">" },
          { value: "lt", label: "<" },
          { value: "gte", label: ">=" },
          { value: "lte", label: "<=" }
        );
      }
      common.push(
        { value: "in", label: "En uno de" },
        { value: "isNull", label: "Es nulo" },
        { value: "isNotNull", label: "No es nulo" }
      );
      return common;
    },
    requiresValue() {
      return this.condition.operator !== "isNull" && this.condition.operator !== "isNotNull";
    },
    inputType() {
      if (this.fieldType === "number") return "number";
      if (this.fieldType === "date") return "date";
      return "text";
    },
    values() {
      return Array.isArray(this.condition.value) ? this.condition.value : [];
    }
  },
  methods: {
    defaultCondition() {
      return { operator: "in", value: [] };
    },
    emitCondition(operator, value) {
      this.$emit("change", { operator, value });
    },
    changeOperator(operator) {
      const value = operator === "in" ? [] : "";
      this.multiInput = "";
      this.emitCondition(operator, value);
    },
    changeValue(value) {
      this.emitCondition(this.condition.operator, value);
    },
    commitValues() {
      const incoming = this.multiInput.split(/[,\n\r]+/).map(value => this.normalizeValue(value)).filter(value => value !== "");
      if (!incoming.length) return;
      const values = Array.from(new Set(this.values.concat(incoming))).slice(0, 50);
      this.multiInput = "";
      this.emitCondition("in", values);
    },
    commitValuesAndApply() {
      if (this.multiInput.trim()) {
        this.commitValues();
        return;
      }
      this.$emit("apply");
    },
    onPaste(event) {
      const text = event.clipboardData && event.clipboardData.getData("text");
      if (!text || !/[,\n\r]/.test(text)) return;
      event.preventDefault();
      this.multiInput = text;
      this.commitValues();
    },
    removeValue(index) {
      const values = this.values.slice();
      values.splice(index, 1);
      this.emitCondition("in", values);
    },
    displayValue(value) {
      if (this.fieldType !== "boolean") return value;
      return value === true || value === "true" ? "Sí" : "No";
    },
    normalizeValue(value) {
      const cleanValue = String(value).trim();
      if (this.fieldType !== "boolean") return cleanValue;
      const normalized = cleanValue.toLowerCase();
      if (["sí", "si", "true", "1"].includes(normalized)) return "true";
      if (["no", "false", "0"].includes(normalized)) return "false";
      return cleanValue;
    },
    // query solo llega desde @search de TagMultiSelect, cuando la lista base
    // vino truncada (distinctExceeded) — sin query, refetchea/mantiene la
    // primera página completa (el proveedor la trata como "sin filtro").
    async loadDistinctValues(query) {
      if (!this.distinctValuesFetcher) return;
      if (this.distinctRequest) this.distinctRequest.abort();
      const request = new AbortController();
      this.distinctRequest = request;
      this.distinctLoading = true;
      try {
        const result = await this.distinctValuesFetcher(this.field, { signal: request.signal, query: query || undefined });
        if (this.distinctRequest !== request) return; // superado por un fetch más nuevo
        this.distinctOptions = result.values || [];
        this.distinctSupported = result.supported;
        this.distinctExceeded = !!result.exceeded;
      } catch (error) {
        if (error && error.name === "AbortError") return;
        this.distinctSupported = false;
      } finally {
        if (this.distinctRequest === request) this.distinctLoading = false;
      }
    },
    onMultiSelectInput(ids) {
      this.emitCondition("in", ids.slice(0, 50));
      if (this.autoApply) this.$emit("apply");
    }
  },
  watch: {
    // Perezoso a propósito: en la tabla hay un AttributeFilter por columna
    // visible, así que precargar dispararía N requests (a ArcGIS, por
    // ejemplo) apenas se abre la tabla. Recién se pide cuando el operador
    // realmente pasa a "in" y todavía no hay opciones cargadas.
    "condition.operator": {
      immediate: true,
      handler(operator) {
        if (operator === "in" && this.distinctValuesFetcher && !this.distinctOptions.length && this.distinctSupported !== false) {
          this.loadDistinctValues();
        }
      }
    },
    // El panel avanzado reutiliza la misma instancia al cambiar de columna
    // (AdvancedAttributeGroup::selectField) - sin este reset, las opciones
    // de la columna anterior quedarían pegadas.
    field() {
      this.distinctOptions = [];
      this.distinctSupported = null;
      this.distinctExceeded = false;
      if (this.condition.operator === "in") this.loadDistinctValues();
    }
  },
  beforeDestroy() {
    if (this.distinctRequest) this.distinctRequest.abort();
  }
};
</script>

<style scoped>
.attribute-filter { display: flex; flex-direction: column; gap: 4px; min-width: 220px; }
.attribute-filter.compact { min-width: 0; display: grid; grid-template-columns: minmax(0, .8fr) minmax(0, 1.2fr); gap: 7px; align-items: start; }
.attribute-filter.compact .filter-control { min-height: 34px; height: 34px; font-size: .8rem; }
.attribute-filter.compact .multi-value-control { min-width: 0; }
.attribute-filter.compact .filter-error { grid-column: 1 / -1; }
.filter-control { width: 100%; min-height: 30px; box-sizing: border-box; border: 1px solid #cbd5e1; border-radius: 4px; padding: 4px 7px; font-size: 0.78rem; background: #fff; }
.filter-control:focus { outline: none; border-color: #3282b8; box-shadow: 0 0 0 2px rgba(50, 130, 184, 0.1); }
.operator-control { font-weight: 600; }
.chips { display: flex; flex-wrap: wrap; gap: 3px; margin-bottom: 3px; max-height: 58px; overflow: auto; }
.chip { display: inline-flex; align-items: center; gap: 4px; max-width: 190px; padding: 2px 5px; border-radius: 10px; background: #e2e8f0; color: #334155; font-size: 0.7rem; overflow: hidden; text-overflow: ellipsis; }
.chip button { border: 0; padding: 0; background: transparent; color: #64748b; cursor: pointer; font-size: 0.9rem; line-height: 1; }
.has-error .filter-control { border-color: #dc2626; }
.filter-error { color: #b91c1c; font-size: 0.68rem; text-align: left; }
@media (max-width: 600px) { .attribute-filter.compact { grid-template-columns: 1fr; } }

/* TagMultiSelect trae su propio diseño (variables --terrain-*), pensado para
   formularios completos, no para encajar en esta fila angosta de 30/34px.
   ::v-deep perfora el scoping para comprimirlo sin tocar el componente
   compartido (también lo usa RecipientPicker.vue). */
.in-multiselect { width: 100%; }
.in-multiselect::v-deep .tag-multi-select__control { padding: 3px 6px; min-height: 30px; }
.attribute-filter.compact .in-multiselect::v-deep .tag-multi-select__control { min-height: 34px; }
.in-multiselect::v-deep .tag-multi-select__input { font-size: 0.78rem; min-width: 40px; }
.in-multiselect::v-deep .tag-multi-select__chips { margin: 0 0 3px; }
.in-multiselect::v-deep .tag-multi-select__chip { font-size: 0.7rem; padding: 1px 4px 1px 6px; }
.in-multiselect::v-deep .tag-multi-select__listbox { max-height: 180px; z-index: 30; }
</style>
